import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/layout/AppShell";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import MyDashboard from "@/pages/MyDashboard";
import Agenda from "@/pages/Agenda";
import Clienti from "@/pages/Clienti";
import Importa from "@/pages/Importa";
import ClientDetail from "@/pages/ClientDetail";
import Cassa from "@/pages/Cassa";
import Team from "@/pages/Team";
import Servizi from "@/pages/Servizi";
import Prodotti from "@/pages/Prodotti";
import Loyalty from "@/pages/Loyalty";
import Pacchetti from "@/pages/Pacchetti";
import Obiettivi from "@/pages/Obiettivi";
import Report from "@/pages/Report";
import Notifiche from "@/pages/Notifiche";
import Marketing from "@/pages/Marketing";
import Retribuzioni from "@/pages/Retribuzioni";
import Impostazioni from "@/pages/Impostazioni";
import { PortalLayout } from "@/components/layout/PortalLayout";
import Register from "@/pages/portal/Register";
import PortalHome from "@/pages/portal/PortalHome";
import PortalBook from "@/pages/portal/PortalBook";
import PortalAppointments from "@/pages/portal/PortalAppointments";
import PortalRewards from "@/pages/portal/PortalRewards";
import PortalWallet from "@/pages/portal/PortalWallet";
import PortalProfile from "@/pages/portal/PortalProfile";
import PortalTransformations from "@/pages/portal/PortalTransformations";
import PortalNotifications from "@/pages/portal/PortalNotifications";
import AuthCallback from "@/pages/AuthCallback";

const FullLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin text-[hsl(var(--hi-gold))]" />
  </div>
);

function Protected({ children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  if (user.role === "client") return <Navigate to="/portale" replace />;
  return <AppShell>{children}</AppShell>;
}

function PortalProtected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "client") return <Navigate to="/" replace />;
  return <PortalLayout>{children}</PortalLayout>;
}

function HomeRedirect() {
  const { user } = useAuth();
  // Fase 5.2.1 — chi non ha la dashboard economica vede la Dashboard Collaboratore
  const perms = user?.permissions || [];
  const canDash = perms.includes("*") || perms.includes("dashboard.read");
  return canDash ? <Dashboard /> : <MyDashboard />;
}

function Shell() {
  const location = useLocation();
  // OAuth Google: se torniamo con #session_id=... gestiamo il callback PRIMA
  // di qualsiasi route protetta (evita race condition con il controllo sessione).
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/registrati" element={<Register />} />

      {/* Portale cliente */}
      <Route path="/portale" element={<PortalProtected><PortalHome /></PortalProtected>} />
      <Route path="/portale/prenota" element={<PortalProtected><PortalBook /></PortalProtected>} />
      <Route path="/portale/appuntamenti" element={<PortalProtected><PortalAppointments /></PortalProtected>} />
      <Route path="/portale/rewards" element={<PortalProtected><PortalRewards /></PortalProtected>} />
      <Route path="/portale/wallet" element={<PortalProtected><PortalWallet /></PortalProtected>} />
      <Route path="/portale/profilo" element={<PortalProtected><PortalProfile /></PortalProtected>} />
      <Route path="/portale/trasformazioni" element={<PortalProtected><PortalTransformations /></PortalProtected>} />
      <Route path="/portale/notifiche" element={<PortalProtected><PortalNotifications /></PortalProtected>} />

      {/* Gestionale staff */}
      <Route path="/" element={<Protected><HomeRedirect /></Protected>} />
      <Route path="/agenda" element={<Protected><Agenda /></Protected>} />
      <Route path="/clienti" element={<Protected><Clienti /></Protected>} />
      <Route path="/clienti/:id" element={<Protected><ClientDetail /></Protected>} />
      <Route path="/importa" element={<Protected><Importa /></Protected>} />
      <Route path="/cassa" element={<Protected><Cassa /></Protected>} />
      <Route path="/team" element={<Protected><Team /></Protected>} />
      <Route path="/servizi" element={<Protected><Servizi /></Protected>} />
      <Route path="/prodotti" element={<Protected><Prodotti /></Protected>} />
      <Route path="/loyalty" element={<Protected><Loyalty /></Protected>} />
      <Route path="/pacchetti" element={<Protected><Pacchetti /></Protected>} />
      <Route path="/obiettivi" element={<Protected><Obiettivi /></Protected>} />
      <Route path="/report" element={<Protected><Report /></Protected>} />
      <Route path="/notifiche" element={<Protected><Notifiche /></Protected>} />
      <Route path="/marketing" element={<Protected><Marketing /></Protected>} />
      <Route path="/retribuzioni" element={<Protected><Retribuzioni /></Protected>} />
      <Route path="/impostazioni" element={<Protected><Impostazioni /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Shell />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
